import { useEffect, useRef, useState } from 'react'
import { throttle } from '../../../utils/utils'

type UseStickyVisibilityBySentinelOptions = {
  throttleDelay?: number
  threshold?: number
}

/** Retry attach while the sentinel mounts after a loading gate (e.g. status skeleton). */
const MAX_ATTACH_FRAMES = 60

export function useStickyVisibilityBySentinel({
  throttleDelay = 0,
  threshold = 0
}: UseStickyVisibilityBySentinelOptions = {}) {
  const [isStickyVisible, setIsStickyVisible] = useState(false)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const throttledVisibilityChangeRef = useRef(
    throttle((isVisible: boolean) => {
      setIsStickyVisible(isVisible)
    }, throttleDelay)
  )

  useEffect(() => {
    let observer: IntersectionObserver | undefined
    let rafId = 0
    let framesTried = 0

    const attach = () => {
      const sentinelElement = sentinelRef.current
      if (!sentinelElement) {
        framesTried += 1
        if (framesTried < MAX_ATTACH_FRAMES) {
          rafId = requestAnimationFrame(attach)
        }
        return
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          throttledVisibilityChangeRef.current(!entry?.isIntersecting)
        },
        { threshold }
      )
      observer.observe(sentinelElement)
    }

    attach()

    return () => {
      cancelAnimationFrame(rafId)
      observer?.disconnect()
    }
  }, [threshold])

  return {
    isStickyVisible,
    sentinelRef
  }
}
